import { useState, useEffect, useCallback } from "react";

import moment from "moment";

import DataTable from "customComponents/Tables/DataTable";
import { useAlert } from "customComponents/Alert";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { formatDate, formatTimeSpent } from "../../../utils";
import { deviceLocation, deviceState, deviceType } from "../../../enums";

import { getIndividualEmployeeReport } from "helper/index";
import config from "../../../config";

import Header from "../components/Header";

import { Link } from "@mui/material";

function MDReport({ token, isToday, onLinkExpired, onToday, isVisible }) {
  const [attendanceData, setAttendanceData] = useState([]);
  const [worklogData, setWorklogData] = useState([]);

  const [employeeData, setEmployeeData] = useState(null);
  const [dateInfo, setDateInfo] = useState(null);

  const [tabValue, setTabValue] = useState(0);

  const alert = useAlert();

  const attendanceColumns = [
    { Header: "No.", accessor: "no", width: "10%", align: "center" },
    { Header: "recordTime", accessor: "recordTime", width: "20%", align: "center" },
    { Header: "state", accessor: "state", align: "center" },
    { Header: "location", accessor: "location", align: "center" },
    { Header: "type", accessor: "type", align: "center" },
  ];

  const worklogColumns = [
    { Header: "No.", accessor: "no", width: "10%", align: "center" },
    { Header: "Project / Issue", accessor: "issue", width: "20%", align: "center" },
    { Header: "Record time", accessor: "recordTime", align: "center" },
    { Header: "Logged", accessor: "timeSpent", align: "center" },
    { Header: "Created At", accessor: "createdAt", align: "center" },
  ];

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  useEffect(() => {
    fetchData();
  }, [token, isToday, isVisible]);

  const fetchData = useCallback(async () => {
    if ((employeeData && dateInfo) || !isVisible) {
      return;
    }

    try {
      const reportDate = isToday ? "today" : null;

      const res = await getIndividualEmployeeReport(token, reportDate);

      const { date, attendances, worklogs, ...rest } = res.data;

      const formattedData = formatData(attendances);
      const formattedWorklogData = formatWorklogData(worklogs);

      const { officeTime, breakTime } = calculateOfficeTime(attendances);
      const worklogHours = worklogs.reduce((total, log) => total + log.timeSpentSeconds, 0);

      setEmployeeData(rest);
      setDateInfo({ date, officeTime, breakTime, worklogHours });

      setAttendanceData(formattedData);
      setWorklogData(formattedWorklogData);
    } catch (error) {
      if (error.response?.status === 401) {
        console.log("link expired");
        onLinkExpired(true);
      } else {
        const title = error.response ? error.response.statusText : "Unknown error";
        const message =
          error.response && error.response.data ? error.response.data.message : error.message;

        alert.show(title, message);
      }
    }
  }, [token, isToday, isVisible]);

  const formatData = (attendances) => {
    const rows = attendances.map((attendance, index) => {
      const { recordTime, device } = attendance;

      return {
        no: (
          <MDTypography variant="caption" fontWeight="medium">
            {index + 1}
          </MDTypography>
        ),
        recordTime: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatDate(recordTime)}
          </MDTypography>
        ),
        state: (
          <MDTypography variant="caption" fontWeight="medium">
            {deviceState.value(device?.state)}
          </MDTypography>
        ),
        location: (
          <MDTypography variant="caption" fontWeight="medium">
            {deviceLocation.value(device?.location)}
          </MDTypography>
        ),
        type: (
          <MDTypography variant="caption" fontWeight="medium">
            {deviceType.value(device?.type)}
          </MDTypography>
        ),
      };
    });

    return rows;
  };

  const calculateOfficeTime = (attendances) => {
    let officeTime = 0;
    let breakTime = 0;

    if (attendances.length > 1) {
      const firstAttendance = attendances[0];
      const latestAttendance = attendances[attendances.length - 1];

      const firstRecordTime = moment(firstAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");
      const latestRecordTime = isToday
        ? moment()
        : moment(latestAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");

      const totalOfficeTime = latestRecordTime.diff(firstRecordTime, "seconds");

      //Admins and office Boys have more check ins and check outs so only calculating their first and last thumb
      if (attendances.length < 30) {
        for (var i = 1; i < attendances.length; i++) {
          const lastAttendance = attendances[i - 1];
          const curAttendance = attendances[i];

          const lastRecordTime = moment(lastAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");
          const curRecordTime = moment(curAttendance.recordTime, "yyyy-MM-DDTHH:mm:ss.SSSZ");

          const diff = curRecordTime.diff(lastRecordTime, "seconds");

          // if diff is greater than or equal to minimum break time
          if (diff >= config.MINIMUM_BREAK_TIME) {
            if (
              lastAttendance.device.state === "checkOut" &&
              curAttendance.device.state === "checkIn"
            ) {
              breakTime += diff;
            } else if (
              (lastAttendance.device.state === "checkOut" &&
                curAttendance.device.state === "checkOut") ||
              (lastAttendance.device.state === "checkIn" &&
                curAttendance.device.state === "checkIn")
            ) {
              breakTime += config.MINIMUM_BREAK_TIME; // assuming here, user has not checked in so he might have spent MINIMUM_BREAK_TIME outside
            }
          }
        }
      }

      officeTime = totalOfficeTime - breakTime;
    }

    return { officeTime, breakTime };
  };

  const formatWorklogData = (res) => {
    return res.map((value, index) => {
      const {
        worklogStarted,
        employee,
        issueSummary,
        issueIdOrKey,
        projectName,
        projectKey,
        timeSpentSeconds,
        worklogCreated,
      } = value;

      return {
        no: (
          <MDTypography variant="caption" fontWeight="medium">
            {index + 1}
          </MDTypography>
        ),
        issue: (
          <Link href={`${config.JIRA_API_URL}/browse/${issueIdOrKey}`} target="_blank">
            <MDBox
              display="flex"
              alignItems="left"
              flexDirection="column"
              lineHeight={1}
              sx={{ maxWidth: "240px" }}
            >
              {/* <MDBox lineHeight={1} ml={0}> */}
              <MDTypography
                variant="caption"
                fontWeight="medium"
                // color="text"
                noWrap={false}
                sx={{ wordBreak: "break-word" }}
              >
                {`${issueIdOrKey}:${issueSummary}`}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {projectName}
              </MDTypography>
              {/* </MDBox> */}
            </MDBox>
          </Link>
        ),
        employeeName: (
          <MDTypography variant="caption" fontWeight="medium">
            {employee?.name}
          </MDTypography>
        ),
        recordTime: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatDate(worklogStarted)}
          </MDTypography>
        ),
        timeSpent: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatTimeSpent(timeSpentSeconds)}
          </MDTypography>
        ),
        createdAt: (
          <MDTypography variant="caption" fontWeight="medium">
            {formatDate(worklogCreated)}
          </MDTypography>
        ),
      };
    });
  };

  if (!isVisible) {
    return null;
  }

  return (
    employeeData &&
    dateInfo && (
      <Header
        employeeData={employeeData}
        dateInfo={dateInfo}
        tabValue={tabValue}
        onToday={onToday}
        isToday={isToday}
        handleSetTabValue={handleSetTabValue}
        hasTabs={worklogData.length > 0}
      >
        {tabValue === 0 ? (
          <DataTable
            table={{ rows: attendanceData, columns: attendanceColumns }}
            showTotalEntries={true}
            isSorted={false}
            noEndBorder
            entriesPerPage={false}
          />
        ) : (
          <DataTable
            table={{ rows: worklogData, columns: worklogColumns }}
            showTotalEntries={true}
            isSorted={false}
            noEndBorder
            entriesPerPage={false}
          />
        )}
      </Header>
    )
  );
}

export default MDReport;
